import { useEffect, useState } from "react";
import { resolveInitialTheme, toggleTheme, type Theme } from "../../lib/theme";

/**
 * Light/dark toggle. Renders as a small icon button sized to sit in the
 * dashboard's top header alongside the existing controls.
 */
export default function ThemeToggle({ className = "" }: { className?: string }) {
  const [theme, setLocalTheme] = useState<Theme>(() => resolveInitialTheme());

  // The bootstrap in main.tsx applies the initial theme synchronously;
  // this effect just keeps the component's local state in sync if some
  // other code changes the attribute.
  useEffect(() => {
    const current = (document.documentElement.getAttribute("data-theme") as Theme) || "dark";
    if (current !== theme) setLocalTheme(current);
  }, [theme]);

  const onClick = () => {
    const next = toggleTheme(theme);
    setLocalTheme(next);
  };

  const isLight = theme === "light";

  return (
    <button
      onClick={onClick}
      aria-label={isLight ? "Switch to dark theme" : "Switch to light theme"}
      title={isLight ? "Switch to dark theme" : "Switch to light theme"}
      className={
        "inline-flex items-center justify-center w-8 h-8 rounded-md " +
        "text-d_text2 hover:text-d_text " +
        "border border-d_border/30 hover:border-d_accent/50 " +
        "bg-d_card2/60 hover:bg-d_card2 " +
        "transition-colors " +
        className
      }
    >
      {isLight ? (
        // moon (clicking will switch to dark)
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      ) : (
        // sun (clicking will switch to light)
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      )}
    </button>
  );
}
